import React from 'react';
import { cn } from '../utils/cn';

export interface PageAccessTab {
  label: string;
  module: string;
  /** The page's own tab id (TabbedPage) when this checkbox is one tab of a
   * permission module that covers several tabs - see app/auth/tab_access.py. */
  tab?: string;
}

export interface PageAccessPage {
  label: string;
  /** The page's route - lets TabbedPage find its own entry to hide tabs. */
  path?: string;
  tabs: PageAccessTab[];
}

/**
 * Every nav page with one checkbox per tab. Access is stored per permission
 * module, plus "Module::tab" keys when only some of a module's tabs are
 * given (the server then refuses changes through the other tabs, and
 * TabbedPage hides them). Ticking every tab of a module stores just the
 * module - exactly how it worked before tabs could be picked one by one.
 * Mirrors navConfig.ts's pages, each page's TabbedPage tab ids, and the
 * backend's tab table (app/auth/tab_access.py's TABS) - keep all three in step.
 */
export const PAGE_ACCESS_MAP: PageAccessPage[] = [
  { label: 'Dashboard', path: '/dashboard', tabs: [{ label: 'Dashboard — farm figures (money & bills need Finance figures, under Reports)', module: 'Dashboard' }] },
  { label: 'Farm Setup', path: '/farm', tabs: [
    { label: 'Farm Details', module: 'System Settings', tab: 'farm' },
    { label: 'Contacts', module: 'System Settings', tab: 'contacts' },
    { label: 'Sheds', module: 'System Settings', tab: 'sheds' },
    { label: 'Locations', module: 'System Settings', tab: 'locations' },
  ] },
  { label: 'Assets Registry', path: '/assets', tabs: [
    { label: 'Assets (same access as Animals)', module: 'Animals' },
    { label: 'Asset Types', module: 'System Settings', tab: 'asset-types' },
    { label: 'Animal Types', module: 'System Settings', tab: 'animal-types' },
    { label: 'Breeds', module: 'System Settings', tab: 'breeds' },
  ] },
  { label: 'Animals', path: '/animals', tabs: [
    { label: 'Animals list & profile, Scan QR, Animal Lifecycle, Cattle Register (all 7 sheets)', module: 'Animals' },
    { label: 'Tags & QR (identification)', module: 'Tags & QR' },
    { label: 'Lifecycle Events', module: 'Animal Lifecycle' },
    { label: 'Movements', module: 'Movement' },
  ] },
  { label: 'Dairy', path: '/dairy', tabs: [
    { label: 'Milk Sessions (and the app\'s Collect Milk)', module: 'Milk', tab: 'sessions' },
    { label: 'Milk Sales (and the app\'s Milk Sale)', module: 'Milk', tab: 'sales' },
    { label: 'Dairy Collection Hubs', module: 'Milk', tab: 'collection-hubs' },
    { label: 'Daily Summary', module: 'Milk', tab: 'summary' },
    { label: 'Lactations', module: 'Milk', tab: 'lactations' },
    { label: 'Cow Profiles', module: 'Milk', tab: 'cow-profiles' },
    { label: 'Calf Records', module: 'Milk', tab: 'calf-records' },
  ] },
  { label: 'Breeding', path: '/breeding', tabs: [
    { label: 'Alerts', module: 'Breeding', tab: 'alerts' },
    { label: 'Heat', module: 'Breeding', tab: 'heat' },
    { label: 'Service (AI / Natural)', module: 'Breeding', tab: 'service' },
    { label: 'Pregnancy (PD checks)', module: 'Pregnancy' },
    { label: 'Calving', module: 'Calving', tab: 'calving' },
    { label: 'Lambing / Kidding', module: 'Calving', tab: 'lambing' },
    { label: 'Semen Catalogue', module: 'Breeding', tab: 'semen-master' },
    { label: 'Semen Inventory', module: 'Breeding', tab: 'semen-inventory' },
    { label: 'KPIs', module: 'Breeding', tab: 'kpis' },
  ] },
  { label: 'Health', path: '/health', tabs: [
    { label: 'Health Records', module: 'Health', tab: 'health-records' },
    { label: 'Vet Visits', module: 'Health', tab: 'vet-visits' },
    { label: 'Treatments', module: 'Treatment' },
    { label: 'Vaccinations', module: 'Vaccination', tab: 'vaccinations' },
    { label: 'Vaccination Schedules', module: 'Vaccination', tab: 'vaccination-schedules' },
    { label: 'Deworming', module: 'Vaccination', tab: 'deworming' },
    { label: 'Disease Outbreaks', module: 'Health', tab: 'outbreaks' },
    { label: 'Growth (weight)', module: 'Weight' },
    { label: 'Medicines', module: 'Health', tab: 'medicines' },
    { label: 'Vaccines', module: 'Health', tab: 'vaccines' },
  ] },
  { label: 'Poultry', path: '/poultry', tabs: [
    { label: 'Batches', module: 'Poultry', tab: 'batches' },
    { label: 'Mortality', module: 'Poultry', tab: 'mortality' },
    { label: 'Eggs', module: 'Poultry', tab: 'eggs' },
    { label: 'Feed', module: 'Poultry', tab: 'feed' },
    { label: 'KPIs', module: 'Poultry', tab: 'kpis' },
  ] },
  { label: 'Coconut', path: '/coconut', tabs: [
    { label: 'Trees / Groups', module: 'Coconut', tab: 'trees' },
    { label: 'Harvests', module: 'Coconut', tab: 'harvests' },
    { label: 'Yield Report', module: 'Coconut', tab: 'yield-report' },
  ] },
  { label: 'Inventory', path: '/inventory', tabs: [
    { label: 'Items', module: 'Inventory', tab: 'items' },
    { label: 'Lots', module: 'Inventory', tab: 'lots' },
    { label: 'Transactions', module: 'Inventory', tab: 'transactions' },
    { label: 'Locations', module: 'Inventory', tab: 'locations' },
    { label: 'Feed Items', module: 'Inventory', tab: 'feed-items' },
    { label: 'Feed Consumption', module: 'Inventory', tab: 'feed-consumption' },
  ] },
  { label: 'Procurement & Finance', path: '/procurement-finance', tabs: [
    { label: 'Suppliers', module: 'Purchases', tab: 'suppliers' },
    { label: 'Purchase Orders', module: 'Purchases', tab: 'purchase-orders' },
    { label: 'Purchases (incl. animal purchases)', module: 'Purchases', tab: 'purchases' },
    { label: 'Expense Categories', module: 'Expenses', tab: 'expense-categories' },
    { label: 'Expenses', module: 'Expenses', tab: 'expenses' },
    { label: 'Other Sales (eggs, coconut…)', module: 'Sales', tab: 'sales' },
  ] },
  { label: 'Tasks', path: '/tasks', tabs: [
    { label: 'Tasks (and the app\'s My Tasks)', module: 'Tasks', tab: 'tasks' },
    { label: 'Daily Log (repeating jobs, reopen)', module: 'Tasks', tab: 'daily-log' },
    { label: 'Assignments', module: 'Tasks', tab: 'assignments' },
  ] },
  { label: 'Enquiries', path: '/enquiries', tabs: [{ label: 'Website enquiries', module: 'Enquiries' }] },
  { label: 'Online Shop & Customers', path: '/online-shop', tabs: [
    { label: 'Shop Products & Prices', module: 'Sales', tab: 'shop-products' },
    { label: 'Subscription Plans', module: 'Sales', tab: 'subscription-plans' },
    { label: 'Customers', module: 'Sales', tab: 'customers' },
    { label: 'Customer website logins (PINs)', module: 'Sales', tab: 'shop-customer-logins' },
    { label: 'Online Orders (verify UPI payments, deliver)', module: 'Sales', tab: 'shop-orders' },
    { label: 'Website payments (UPI, to check)', module: 'Sales', tab: 'shop-account-payments' },
    { label: 'Customer Payments', module: 'Sales', tab: 'customer-payments' },
    { label: 'Hub Payments (Dairy Collection Hub, 1–15 / 16–end)', module: 'Sales', tab: 'hub-payments' },
    { label: 'Customer Accounts', module: 'Sales', tab: 'customer-accounts' },
    { label: 'Shop Settings (UPI ID, delivery)', module: 'Sales', tab: 'shop-settings' },
  ] },
  { label: 'Deliveries (agent page / mobile app)', tabs: [{ label: 'Delivery route, agent page (/agent), Verify Deliveries, Agents (live), Spillage, Milk Balance Check — and Delivery Changes', module: 'Deliveries' }] },
  { label: 'Utilities', path: '/utilities', tabs: [
    { label: 'Meters', module: 'Utilities', tab: 'meters' },
    { label: 'Daily Readings', module: 'Utilities', tab: 'readings' },
  ] },
  { label: 'Administration', path: '/admin', tabs: [
    { label: 'Users', module: 'Users', tab: 'users' },
    { label: 'Roles & Permissions', module: 'Roles & Permissions', tab: 'roles-permissions' },
    { label: 'User Roles', module: 'Roles & Permissions', tab: 'user-roles' },
    { label: 'Sessions', module: 'Users', tab: 'sessions' },
    { label: 'Notification Rules', module: 'Notifications' },
    { label: 'Settings', module: 'System Settings', tab: 'settings' },
    { label: 'Appearance (text size, dates, labels)', module: 'System Settings', tab: 'appearance' },
    { label: 'Entry Form Settings (required / hidden fields)', module: 'System Settings', tab: 'entry-forms' },
    { label: 'Audit Log', module: 'Audit Logs' },
  ] },
  { label: 'Reports', path: '/reports', tabs: [
    { label: 'Milk Production, Milk Sales by Customer, Partner / Vendor / Customer / Calves / Office', module: 'Operational Reports' },
    { label: 'Finance figures (money & bills on the Dashboard)', module: 'Finance Reports' },
  ] },
];

// Always kept for a restricted user regardless of what's checked — but only
// modules with no checkbox of their own to grant them (a restriction can't
// otherwise block photo/document uploads or the notification bell, since
// neither has a dedicated page in the list above). Dashboard is NOT included
// here on request: "only assigned pages" means literally that, and Dashboard
// has its own checkbox above if it should be one of them — see Login.tsx
// for where a restricted user who didn't check it lands instead.
export const ALWAYS_INCLUDED_MODULES = ['Attachments', 'Notifications'];

const SEP = '::';
const ALL_ENTRIES = PAGE_ACCESS_MAP.flatMap((p) => p.tabs);
/** Every tab id of a module, across all pages (System Settings spans three). */
const tabsOfModule = (module: string) => ALL_ENTRIES.filter((t) => t.module === module && t.tab).map((t) => t.tab!);

/** The tabs of `module` a stored access list allows (all of them when the
 * module is there with no tab keys). */
export function allowedTabs(value: string[], module: string): Set<string> {
  if (!value.includes(module)) return new Set();
  const keys = value.filter((v) => v.startsWith(module + SEP)).map((v) => v.slice(module.length + SEP.length));
  return new Set(keys.length ? keys : tabsOfModule(module));
}

/** Should this signed-in user see a page's tab? Unrestricted users see all. */
export function tabVisible(restricted: string[] | undefined, path: string, tabId: string): boolean {
  if (!restricted?.length) return true;
  const page = PAGE_ACCESS_MAP.find((p) => p.path && (path === p.path || path.startsWith(p.path + '/')));
  const entry = page?.tabs.find((t) => t.tab === tabId);
  if (!entry) return true;
  return allowedTabs(restricted, entry.module).has(tabId);
}

function withTabs(value: string[], module: string, tabs: Set<string>): string[] {
  const rest = value.filter((v) => v !== module && !v.startsWith(module + SEP));
  if (!tabs.size) return rest;
  const all = tabsOfModule(module);
  if (all.every((t) => tabs.has(t))) return [...rest, module];
  return [...rest, module, ...all.filter((t) => tabs.has(t)).map((t) => `${module}${SEP}${t}`)];
}

function isOn(value: string[], entry: PageAccessTab): boolean {
  return entry.tab ? allowedTabs(value, entry.module).has(entry.tab) : value.includes(entry.module);
}

function setEntry(value: string[], entry: PageAccessTab, on: boolean): string[] {
  if (entry.tab) {
    const tabs = allowedTabs(value, entry.module);
    if (on) tabs.add(entry.tab);
    else tabs.delete(entry.tab);
    return withTabs(value, entry.module, tabs);
  }
  if (on) return value.includes(entry.module) ? value : [...value, entry.module];
  return value.filter((v) => v !== entry.module && !v.startsWith(entry.module + SEP));
}

export function ILMSPageAccessPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const isPageFullyChecked = (page: PageAccessPage) => page.tabs.every((t) => isOn(value, t));
  const isPagePartiallyChecked = (page: PageAccessPage) => page.tabs.some((t) => isOn(value, t)) && !isPageFullyChecked(page);
  const togglePage = (page: PageAccessPage) => {
    const on = !isPageFullyChecked(page);
    onChange(page.tabs.reduce((acc, t) => setEntry(acc, t, on), value));
  };

  return (
    <div className="flex flex-col gap-2 max-h-80 overflow-y-auto border border-ink-300/20 rounded-md p-3">
      {value.length === 0 && (
        <p className="text-helper-text text-ink-500 pb-1">
          No pages selected — this user will see every page their Role(s) already allow (unrestricted).
        </p>
      )}
      {PAGE_ACCESS_MAP.map((page) => (
        <div key={page.label} className="border-b border-ink-300/10 last:border-0 pb-2 last:pb-0">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPageFullyChecked(page)}
              ref={(el) => { if (el) el.indeterminate = isPagePartiallyChecked(page); }}
              onChange={() => togglePage(page)}
              className="h-4 w-4 rounded border-ink-300/50 text-brand-green-700 focus-visible:outline-2 focus-visible:outline-brand-green-500"
            />
            <span className="text-body font-medium text-ink-800">{page.label}</span>
          </label>
          {/* A one-checkbox page still names what it covers. */}
          {page.tabs.length === 1 && page.tabs[0].label !== page.label && (
            <p className="ml-6 mt-0.5 text-helper-text text-ink-500">{page.tabs[0].label}</p>
          )}
          {page.tabs.length > 1 && (
            <div className="flex flex-col gap-1 mt-1 ml-6">
              {page.tabs.map((entry) => (
                <label key={`${entry.module}-${entry.tab ?? ''}`} className={cn('flex items-center gap-2 cursor-pointer text-secondary text-ink-600')}>
                  <input
                    type="checkbox"
                    checked={isOn(value, entry)}
                    onChange={() => onChange(setEntry(value, entry, !isOn(value, entry)))}
                    className="h-3.5 w-3.5 rounded border-ink-300/50 text-brand-green-700 focus-visible:outline-2 focus-visible:outline-brand-green-500"
                  />
                  {entry.label}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
